import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import axios from "../../../utills/api";
import dayjs from "dayjs";

const BalanceOverview = () => {
  const [chartData, setChartData] = useState([
    {
      name: "Revenue",
      data: Array(12).fill(0),
    },
    {
      name: "Outstanding",
      data: Array(12).fill(0),
    },
  ]);

  const [totalPaid, setTotalPaid] = useState(0);
  const [totalUnpaid, setTotalUnpaid] = useState(0);
  const [profitRatio, setProfitRatio] = useState(0);
  const [selectedPeriod, setSelectedPeriod] =
    useState("currentYear");
  const [loading, setLoading] = useState(true);

  const fetchBalanceData = async () => {
    setLoading(true);

    try {
      const rangeMap = {
        today: "today",
        lastWeek: "week",
        lastMonth: "month",
        currentYear: "year",
      };

      const range =
        rangeMap[selectedPeriod] || "month";

      const { data } = await axios.get(
        `/revenue/summary?range=${range}`
      );

      const summary = data.summary || {};

      console.log("Revenue Summary", summary);

      //---------------------------------------
      // Top Cards
      //---------------------------------------

      const revenue = Number(
        summary.totalRevenue || 0
      );

      const outstanding = Number(
        summary.outstanding || 0
      );

      setTotalPaid(revenue);
      setTotalUnpaid(outstanding);

      const ratio =
        revenue + outstanding > 0
          ? (
              (revenue /
                (revenue + outstanding)) *
              100
            ).toFixed(1)
          : 0;

      setProfitRatio(ratio);

      //---------------------------------------
      // Revenue Trend Chart
      //---------------------------------------

      const revenueData = Array(12).fill(0);
      const outstandingData = Array(12).fill(0);

      (summary.revenueTrend || []).forEach(
        (item) => {
          if (!item.date) return;

          // API returns 24/7/2026
          const parts = item.date.split("/");

          if (parts.length !== 3) return;

          const month =
            parseInt(parts[1], 10) - 1;

          if (
            month >= 0 &&
            month <= 11
          ) {
            revenueData[month] += Number(
              item.amount || 0
            );
          }
        }
      );

      // Outstanding appears in current month
      outstandingData[
        new Date().getMonth()
      ] = outstanding;

      console.log("Revenue Chart", revenueData);
      console.log(
        "Outstanding Chart",
        outstandingData
      );

      setChartData([
        {
          name: "Revenue",
          data: revenueData,
        },
        {
          name: "Outstanding",
          data: outstandingData,
        },
      ]);
    } catch (err) {
      console.error(
        "Balance Overview Error",
        err
      );

      setTotalPaid(0);
      setTotalUnpaid(0);
      setProfitRatio(0);

      setChartData([
        {
          name: "Revenue",
          data: Array(12).fill(0),
        },
        {
          name: "Outstanding",
          data: Array(12).fill(0),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalanceData();
  }, [selectedPeriod]);

  const onChangeChartPeriod = (
    period
  ) => {
    setSelectedPeriod(period);
  };

  const chartOptions = {
    chart: {
      height: 290,
      type: "area",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },

    dataLabels: {
      enabled: false,
    },

    stroke: {
      curve: "smooth",
      width: 3,
    },

    colors: [
      "#1cc88a",
      "#e74a3b",
    ],

    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.05,
      },
    },

    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      labels: {
        style: {
          fontSize: "12px",
        },
      },
    },

    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: function (val) {
          return Number(val).toLocaleString(
            "en-IN"
          );
        },
        style: {
          fontSize: "12px",
        },
      },
    },

    legend: {
      position: "bottom",
      horizontalAlign: "center",
    },

    tooltip: {
      y: {
        formatter: function (val) {
          return "₹ " + Number(val).toLocaleString("en-IN");
        },
      },
    },

    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 4,
    },
  };
    return (
    <React.Fragment>
      <Col lg={6}>
        <Card className="card-height-100">

          <CardHeader className="align-items-center d-flex">
            <h4 className="card-title mb-0 flex-grow-1">
              Balance Overview
            </h4>

            <div className="flex-shrink-0">
              <UncontrolledDropdown className="card-header-dropdown">
                <DropdownToggle
                  className="text-reset dropdown-btn"
                  tag="a"
                  role="button"
                >
                  <span className="fw-bold text-uppercase fs-12">
                    Sort by:
                  </span>

                  <span className="text-muted ms-1">
                    {selectedPeriod
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, c => c.toUpperCase())}

                    <i className="mdi mdi-chevron-down ms-1"></i>
                  </span>
                </DropdownToggle>

                <DropdownMenu className="dropdown-menu-end">

                  <DropdownItem
                    active={selectedPeriod === "today"}
                    onClick={() =>
                      onChangeChartPeriod("today")
                    }
                  >
                    Today
                  </DropdownItem>

                  <DropdownItem
                    active={selectedPeriod === "lastWeek"}
                    onClick={() =>
                      onChangeChartPeriod("lastWeek")
                    }
                  >
                    Last Week
                  </DropdownItem>

                  <DropdownItem
                    active={selectedPeriod === "lastMonth"}
                    onClick={() =>
                      onChangeChartPeriod("lastMonth")
                    }
                  >
                    Last Month
                  </DropdownItem>

                  <DropdownItem
                    active={selectedPeriod === "currentYear"}
                    onClick={() =>
                      onChangeChartPeriod("currentYear")
                    }
                  >
                    Current Year
                  </DropdownItem>

                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </CardHeader>

          <CardBody>

            <ul className="list-inline main-chart text-center mb-4">

              <li className="list-inline-item me-5">

                <h4 className="text-success fw-bold mb-1">
                  ₹{Number(totalPaid).toLocaleString("en-IN")}
                </h4>

                <span className="text-muted">
                  Revenue
                </span>

              </li>

              <li className="list-inline-item me-5">

                <h4 className="text-danger fw-bold mb-1">
                  ₹{Number(totalUnpaid).toLocaleString("en-IN")}
                </h4>

                <span className="text-muted">
                  Outstanding
                </span>

              </li>

              <li className="list-inline-item">

                <h4 className="text-primary fw-bold mb-1">
                  {profitRatio}%
                </h4>

                <span className="text-muted">
                  Collection Ratio
                </span>

              </li>

            </ul>

            <div
              id="balance-overview-chart"
              dir="ltr"
            >

              {loading ? (

                <div
                  className="text-center py-5"
                >
                  Loading...
                </div>

              ) : (

                <ReactApexChart
                  options={chartOptions}
                  series={chartData}
                  type="area"
                  height={320}
                />

              )}

            </div>

          </CardBody>

        </Card>
      </Col>
    </React.Fragment>
  );
};

export default BalanceOverview;